import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import * as R from 'remeda';

import Autocomplete from '@material-ui/lab/Autocomplete';

import { Language } from '../../utils/language';
import { Fylke, Kommune, FylkeMedKommune, Omraader } from './types';
import { Maybe } from 'purify-ts';
import { Field, updateValue } from '../formTypes';

import { RemoteData } from '../../utils/remoteData';

interface KommuneSelectProps {
  language: Language;
  selectedFylker: Field<Fylke[]>;
  selectedKommuner: Field<Kommune[]>;
  omraader: RemoteData<Error, Omraader>;
  setSelectedKommuner: (kommuner: Field<Kommune[]>) => void;
  variant?: 'filled' | 'standard' | 'outlined' | undefined;
}

function getKommuneFromFylke(selectedFylker: Fylke[], fylkerMedKommuner: FylkeMedKommune[]): Kommune[] {
  const selectedFylkeNr = R.map(selectedFylker, f => f.nummer);
  const selectedFylkerMedKommuner = R.pipe(
    fylkerMedKommuner,
    R.filter(f => selectedFylkeNr.includes(f.nummer)),
  );

  return R.pipe(
    selectedFylkerMedKommuner,
    R.flatMap(sf => sf.kommuner),
  );
}

function visibleKommuner(
  selectedFylker: Fylke[],
  fylkerMedKommuner: FylkeMedKommune[],
  allKommuner: Kommune[],
): Kommune[] {
  return Maybe.fromPredicate(selFylker => selFylker.length > 0, selectedFylker).caseOf({
    Nothing: () => allKommuner,
    Just: fylker => getKommuneFromFylke(fylker, fylkerMedKommuner),
  });
}

function getKommuneFromName(name: string, allKommuner: Kommune[]): Kommune | undefined {
  return allKommuner.find(kommune => kommune.navn === name);
}

function KommuneSelectNew({
  language,
  selectedFylker,
  setSelectedKommuner,
  selectedKommuner,
  omraader,
  variant = 'filled',
}: KommuneSelectProps) {
  const kommuneOptions =
    omraader.type === 'SUCCESS'
      ? visibleKommuner(selectedFylker.value, omraader.data.fylkerMedKommuner, omraader.data.kommuner)
      : [];

  return (
    <FormControl
      disabled={omraader.type !== 'SUCCESS'}
      required
      error={selectedKommuner.value.length === 0}
      variant={variant}
      style={{ width: '100%' }}
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        autoHighlight
        clearText="Nullstill"
        closeText="Lukk"
        openText={language === 'nb_NO' ? 'Åpne' : 'Opne'}
        value={R.map(selectedKommuner.value, k => k.navn)}
        options={R.pipe(
          kommuneOptions,
          R.map(k => k.navn),
          R.sortBy(R.identity),
        )}
        onChange={(event, value) => {
          if (omraader.type === 'SUCCESS') {
            const newSelection = R.pipe(
              value,
              R.map(k => getKommuneFromName(k, omraader.data.kommuner)),
              R.compact,
            );

            setSelectedKommuner(updateValue(selectedKommuner, newSelection));
          }
        }}
        renderInput={params => (
          <TextField
            {...params}
            label={language === 'nb_NO' ? 'Velg kommune' : 'Vel kommune'}
            variant={variant}
            name="selectedKommuner"
            fullWidth
          />
        )}
      />
    </FormControl>
  );
}

export default KommuneSelectNew;
